import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import Swal from "sweetalert2";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Separator } from "../ui/separator";
import { toast } from "react-toastify";
import apiService from "../../services/api.service";
import logger from "../../utils/logger";

const AddOpponentModal = ({
  isOpen,
  onClose,
  onOpponentAdded,
  zIndex = 1000,
  showSuccessMessage = true,
  autoNextStep = false,
}) => {
  // تم إزالة متغيرات القضايا لأن الخصوم لم يعودوا مرتبطين بالقضايا مباشرة
  const [formData, setFormData] = useState({
    name: "",
    opponent_type: "مدعى عليه",
    phone: "",
    email: "",
    address: "",
    lawyer_name: "",
    lawyer_phone: "",
    lawyer_office: "",
    notes: "",
    status: "active",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // تم إزالة جلب القضايا لأن الخصوم لم يعودوا مرتبطين بالقضايا مباشرة

  // تم إزالة تحديث حقول القضية لأن الخصوم لم يعودوا مرتبطين بالقضايا مباشرة

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // مسح الخطأ عند تعديل الحقل
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSelectChange = (name, value) => {
    logger.debug("handleSelectChange called with:", name, value);
    logger.debug("Before update, formData:", formData);

    // تحديث القيمة مباشرة
    setFormData((prev) => ({ ...prev, [name]: value }));

    // مسح الخطأ عند تعديل الحقل
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    logger.debug("Validating form with formData:", formData);
    const newErrors = {};

    if (!formData.name || formData.name.trim() === "") {
      newErrors.name = "اسم الخصم مطلوب";
    }

    // تم إزالة التحقق من القضية لأن الخصوم لم يعودوا مرتبطين بالقضايا مباشرة
    // تم إزالة التحقق من القضية

    if (
      formData.phone &&
      formData.phone.trim() !== "" &&
      !/^\d+$/.test(formData.phone)
    ) {
      newErrors.phone = "رقم الهاتف يجب أن يحتوي على أرقام فقط";
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "البريد الإلكتروني غير صحيح";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // إعداد البيانات للإرسال، مع استبعاد الحقول الفارغة التي قد تسبب مشاكل في الخادم
      const {
        phone,
        email,
        address,
        lawyer_name,
        lawyer_phone,
        lawyer_office,
        notes,
        ...requiredFields
      } = formData;

      const dataToSend = {
        ...requiredFields,
      };

      // تم إزالة حقول القضية من الإرسال

      // إضافة الحقول الاختيارية فقط إذا كانت لها قيمة
      if (phone && phone.trim() !== "") dataToSend.phone = phone;

      if (email && email.trim() !== "") dataToSend.email = email;

      if (address && address.trim() !== "") dataToSend.address = address;

      // تم إزالة إرسال بيانات القضية
      if (lawyer_name && lawyer_name.trim() !== "")
        dataToSend.lawyer_name = lawyer_name;

      if (lawyer_phone && lawyer_phone.trim() !== "")
        dataToSend.lawyer_phone = lawyer_phone;

      if (lawyer_office && lawyer_office.trim() !== "")
        dataToSend.lawyer_office = lawyer_office;

      if (notes && notes.trim() !== "") dataToSend.notes = notes;

      logger.debug("Submitting opponent data:", dataToSend);
      const response = await apiService.opponents.createOpponent(dataToSend);
      logger.debug("Server response:", response);

      // التحقق من نجاح الإضافة ووجود معرف الخصم
      const isSuccess =
        response.success && (response.data?.id || response.data?._id);

      if (isSuccess) {
        // عرض رسالة نجاح عند إضافة خصم جديد
        if (showSuccessMessage) {
          await Swal.fire({
            title: "تمت الإضافة بنجاح",
            html: `
              تم إضافة الخصم <strong>${dataToSend.name}</strong> بنجاح.<br>
              ${dataToSend.phone ? `رقم الهاتف: ${dataToSend.phone}<br>` : ""}
              ${
                dataToSend.email ? `البريد الإلكتروني: ${dataToSend.email}` : ""
              }
            `,
            icon: "success",
            confirmButtonText: "حسناً",
            timer: 3000,
            timerProgressBar: true,
          });
        }
        setFormData({
          name: "",
          opponent_type: "مدعى عليه",
          phone: "",
          email: "",
          address: "",
          lawyer_name: "",
          lawyer_phone: "",
          lawyer_office: "",
          notes: "",
          status: "active",
        });
        setErrors({});

        // التأكد من وجود معرف الخصم في البيانات المرجعة
        if (!response.data?.id && !response.data?._id) {
          logger.error("No opponent ID in response:", response);
          throw new Error("لم يتم العثور على معرف الخصم في البيانات المرجعة");
        }

        // استخراج بيانات الخصم من الاستجابة
        const opponentData = {
          id: response.data.id || response.data._id,
          name: dataToSend.name,
          opponent_type: dataToSend.opponent_type,
          phone: dataToSend.phone,
          email: dataToSend.email,
          address: dataToSend.address,
          lawyer_name: dataToSend.lawyer_name,
          lawyer_phone: dataToSend.lawyer_phone,
          lawyer_office: dataToSend.lawyer_office,
          notes: dataToSend.notes,
          status: dataToSend.status,
        };
        logger.debug("Extracted opponent data:", opponentData);

        if (onOpponentAdded && typeof onOpponentAdded === "function") {
          logger.debug("Calling onOpponentAdded callback");
          onOpponentAdded(opponentData, autoNextStep);
        }

        logger.debug("Calling onClose callback");
        onClose();
      } else {
        toast.error(response.message || "حدث خطأ أثناء إضافة الخصم");
      }
    } catch (error) {
      logger.error("Error adding opponent:", error);
      logger.error("Error response:", error.response?.data);
      if (error.response?.data?.errors) {
        error.response.data.errors.forEach((err) =>
          logger.error("Validation error:", err)
        );
      }
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.errors?.[0]?.msg ||
          "حدث خطأ أثناء إضافة الخصم"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
      style={{ zIndex: zIndex }}
      dir="rtl"
    >
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>إضافة خصم جديد</CardTitle>
          <Button variant="ghost" onClick={onClose}>
            ×
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">اسم الخصم *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={errors.name ? "border-red-500" : ""}
                />
                {errors.name && (
                  <p className="text-red-500 text-sm">{errors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">رقم الهاتف</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={errors.phone ? "border-red-500" : ""}
                />
                {errors.phone && (
                  <p className="text-red-500 text-sm">{errors.phone}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">البريد الإلكتروني</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={errors.email ? "border-red-500" : ""}
                />
                {errors.email && (
                  <p className="text-red-500 text-sm">{errors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">العنوان</Label>
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="status">الحالة</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => handleSelectChange("status", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="اختر الحالة" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">نشط</SelectItem>
                    <SelectItem value="settled">تم تسوية</SelectItem>
                    <SelectItem value="inactive">غير نشط</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="opponent_type">نوع الخصم</Label>
                <Select
                  value={formData.opponent_type}
                  onValueChange={(value) =>
                    handleSelectChange("opponent_type", value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="اختر نوع الخصم" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="مدعى عليه">مدعى عليه</SelectItem>
                    <SelectItem value="مدعي">مدعي</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator className="my-4" />

            <div className="space-y-4">
              <h3 className="text-lg font-medium">معلومات المحامي/الوكيل</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="lawyer_name">اسم المحامي/الوكيل</Label>
                  <Input
                    id="lawyer_name"
                    name="lawyer_name"
                    value={formData.lawyer_name}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lawyer_phone">هاتف المحامي</Label>
                  <Input
                    id="lawyer_phone"
                    name="lawyer_phone"
                    value={formData.lawyer_phone}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="lawyer_office">مكتب المحاماة</Label>
                <Input
                  id="lawyer_office"
                  name="lawyer_office"
                  value={formData.lawyer_office}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <Separator className="my-4" />

            <div className="space-y-2">
              <Label htmlFor="notes">ملاحظات</Label>
              <Textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={3}
              />
            </div>

            <div className="flex justify-end space-x-2 pt-4" dir="ltr">
              <Button type="button" variant="outline" onClick={onClose}>
                إلغاء
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "جاري الإضافة..." : "إضافة خصم"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddOpponentModal;
